import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, GraduationCap, Building2, Users2, UserCog } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Subscription() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [subType, setSubType] = useState('academic'); // 'academic' | 'corporate'

  // Academic per-ID state
  const [region, setRegion] = useState('india'); // 'india' | 'international' (applies to both flows)
  const [category, setCategory] = useState('student'); // 'student' | 'faculty'
  const [quantity, setQuantity] = useState(1);
  const [instituteName, setInstituteName] = useState('');
  const [instituteEmail, setInstituteEmail] = useState('');

  // Corporate state (unchanged model)
  const [selectedTenure, setSelectedTenure] = useState('6_months');

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/pricing`).then((r) => setPricing(r.data)).catch(() => toast.error('Failed to load pricing'));
  }, []);

  const currency = region === 'india' ? 'INR' : 'USD';
  const currencySymbol = region === 'india' ? '\u20B9' : '$';

  const perIdPrice = () => {
    if (!pricing) return 0;
    return region === 'india' ? (pricing.academic_inr_per_id ?? 500) : (pricing.academic_usd_per_id ?? 10);
  };

  const academicTotal = perIdPrice() * Math.max(1, quantity);

  const corpPackages = () => {
    if (!pricing) return [];
    if (region === 'india') {
      return [
        { id: '6_months', label: '6 Months', price: pricing.corp_inr_six_months || 85000, description: 'Half-year corporate access', popular: true },
        { id: '12_months', label: '12 Months', price: pricing.corp_inr_twelve_months || 150000, description: 'Full-year corporate access', popular: false },
      ];
    }
    return [
      { id: '6_months', label: '6 Months', price: pricing.corp_usd_six_months || 1500, description: 'Half-year corporate access', popular: true },
      { id: '12_months', label: '12 Months', price: pricing.corp_usd_twelve_months || 2500, description: 'Full-year corporate access', popular: false },
    ];
  };

  const formatPrice = (price) => {
    if (region === 'india') return `\u20B9${price?.toLocaleString('en-IN')}`;
    return `$${price?.toLocaleString('en-US')}`;
  };

  const handleAcademicSubscribe = async () => {
    if (!user) {
      toast.info('Please sign in or register to continue with your subscription.');
      navigate('/login?redirect=/subscription');
      return;
    }
    if (!instituteName.trim() || !instituteEmail.trim()) {
      toast.error('Please enter your institute name and email');
      return;
    }
    if (quantity < 1) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/academic-ids`, {
        institute_name: instituteName.trim(),
        institute_email: instituteEmail.trim().toLowerCase(),
        category,
        quantity: parseInt(quantity, 10),
        currency,
      });
      const { order_id, amount, key_id, user_name, user_email, package_label } = response.data;
      const options = {
        key: key_id,
        amount,
        currency: response.data.currency,
        name: 'The Case Verse',
        description: package_label,
        order_id,
        prefill: { name: user_name || instituteName, email: user_email || instituteEmail },
        theme: { color: '#0f172a' },
        handler: async function (razorpayResponse) {
          try {
            const verifyRes = await axios.post(`${API}/checkout/verify`, {
              razorpay_order_id: razorpayResponse.razorpay_order_id,
              razorpay_payment_id: razorpayResponse.razorpay_payment_id,
              razorpay_signature: razorpayResponse.razorpay_signature,
            });
            toast.success(verifyRes.data?.message || 'Payment successful. IDs emailed to your institute.');
          } catch (err) {
            toast.error('Payment verification failed. Please contact support.');
          } finally {
            setLoading(false);
          }
        },
        modal: { ondismiss: () => { setLoading(false); toast.error('Payment cancelled'); } },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (res) => { toast.error('Payment failed: ' + (res.error?.description || 'Unknown error')); setLoading(false); });
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
    }
  };

  const handleCorporateSubscribe = async () => {
    if (!user) {
      toast.info('Please sign in or register to continue with your subscription.');
      navigate('/login?redirect=/subscription');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/checkout`, {
        package_id: selectedTenure,
        origin_url: window.location.origin,
        currency,
        subscriber_type: 'corporate',
      });
      const { order_id, amount, key_id, user_name, user_email, package_label } = response.data;
      const options = {
        key: key_id,
        amount,
        currency: response.data.currency,
        name: 'The Case Verse',
        description: `Subscription - ${package_label}`,
        order_id,
        prefill: { name: user_name, email: user_email },
        theme: { color: '#0f172a' },
        handler: async function (razorpayResponse) {
          try {
            await axios.post(`${API}/checkout/verify`, {
              razorpay_order_id: razorpayResponse.razorpay_order_id,
              razorpay_payment_id: razorpayResponse.razorpay_payment_id,
              razorpay_signature: razorpayResponse.razorpay_signature,
            });
            toast.success('Payment successful! Your subscription is now active.');
          } catch (err) {
            toast.error('Payment verification failed. Please contact support.');
          } finally { setLoading(false); }
        },
        modal: { ondismiss: () => { setLoading(false); toast.error('Payment cancelled'); } },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (res) => { toast.error('Payment failed: ' + (res.error?.description || 'Unknown error')); setLoading(false); });
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="fixed top-0 w-full z-50 glassmorphism">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-2xl font-serif font-bold tracking-tight text-slate-900" data-testid="logo-link">The Case Verse</Link>
            <div className="flex items-center gap-6">
              {user ? (
                <>
                  <Link to="/cases" className="text-slate-700 hover:text-slate-900" data-testid="nav-cases-link">Case Library</Link>
                  <Link to="/dashboard" className="text-slate-700 hover:text-slate-900" data-testid="nav-dashboard-link">Dashboard</Link>
                  <button onClick={logout} className="px-4 py-2 bg-slate-900 text-white rounded hover:bg-slate-800" data-testid="logout-button">Logout</button>
                </>
              ) : (
                <Link to="/login" className="px-4 py-2 bg-slate-900 text-white rounded hover:bg-slate-800" data-testid="nav-login-link">Login / Sign Up</Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-5xl font-serif font-bold tracking-tight text-slate-900 mb-4" data-testid="subscription-title">Choose Your Plan</h1>
            <p className="text-lg text-slate-600">
              Secure payment powered by Razorpay.
              {region === 'india' ? ' All prices in Indian Rupees. 18% GST inclusive.' : ' All prices in US Dollars.'}
            </p>
          </div>

          {/* Subscriber Type Toggle */}
          <div className="flex items-center justify-center gap-3 mb-6" data-testid="subscriber-type-toggle">
            <button
              onClick={() => setSubType('academic')}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl border-2 transition-all text-sm font-medium ${subType === 'academic' ? 'border-slate-900 bg-slate-900 text-white shadow-lg' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400'}`}
              data-testid="subtype-academic-btn"
            >
              <GraduationCap className="w-5 h-5" /> Academic Institutions
            </button>
            <button
              onClick={() => setSubType('corporate')}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl border-2 transition-all text-sm font-medium ${subType === 'corporate' ? 'border-slate-900 bg-slate-900 text-white shadow-lg' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400'}`}
              data-testid="subtype-corporate-btn"
            >
              <Building2 className="w-5 h-5" /> Corporate L&D
            </button>
          </div>

          {/* Region toggle */}
          <div className="flex items-center justify-center gap-2 mb-10" data-testid="region-toggle">
            <div className="inline-flex bg-slate-200 rounded-lg p-1">
              <button
                onClick={() => setRegion('india')}
                className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${region === 'india' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                data-testid="region-india-btn"
              >India (INR)</button>
              <button
                onClick={() => setRegion('international')}
                className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${region === 'international' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                data-testid="region-intl-btn"
              >International (USD)</button>
            </div>
          </div>

          {/* Section header — shows for BOTH academic and corporate now */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-serif font-bold text-slate-900" data-testid="section-header">
              {subType === 'academic'
                ? (region === 'india' ? 'Academic Institution - India (INR)' : 'Academic Institution - International (USD)')
                : (region === 'india' ? 'Corporate - India (INR)' : 'Corporate - International (USD)')}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {subType === 'academic'
                ? 'Pay per access ID. Your institute distributes IDs to students & faculty.'
                : 'Institution-wide access for your L&D team.'}
            </p>
          </div>

          {subType === 'academic' ? (
            <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-slate-200 shadow-sm p-8" data-testid="academic-per-id-panel">
              {/* Per-ID price card */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <button
                  onClick={() => setCategory('student')}
                  className={`p-5 rounded-xl border-2 text-left transition-all ${category === 'student' ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid="category-student-btn"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Users2 className="w-5 h-5 text-orange-600" />
                    <span className="font-medium">Student ID</span>
                    {category === 'student' && <Check className="w-4 h-4 text-slate-900 ml-auto" />}
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{formatPrice(perIdPrice())}<span className="text-sm font-normal text-slate-500"> / ID</span></p>
                  <p className="text-xs text-slate-500 mt-1">Access to cases + student worksheets</p>
                </button>
                <button
                  onClick={() => setCategory('faculty')}
                  className={`p-5 rounded-xl border-2 text-left transition-all ${category === 'faculty' ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid="category-faculty-btn"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <UserCog className="w-5 h-5 text-orange-600" />
                    <span className="font-medium">Faculty ID</span>
                    {category === 'faculty' && <Check className="w-4 h-4 text-slate-900 ml-auto" />}
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{formatPrice(perIdPrice())}<span className="text-sm font-normal text-slate-500"> / ID</span></p>
                  <p className="text-xs text-slate-500 mt-1">Cases + teaching notes + submitted worksheets</p>
                </button>
              </div>

              {/* Form */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Institute name</label>
                  <input
                    type="text"
                    required
                    value={instituteName}
                    onChange={(e) => setInstituteName(e.target.value)}
                    placeholder="e.g. IIM Bangalore"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                    data-testid="institute-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Institute email</label>
                  <input
                    type="email"
                    required
                    value={instituteEmail}
                    onChange={(e) => setInstituteEmail(e.target.value)}
                    placeholder="learn@yourinstitute.edu"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                    data-testid="institute-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Number of IDs</label>
                  <input
                    type="number"
                    min={1}
                    max={500}
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, Math.min(500, parseInt(e.target.value || '1', 10))))}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                    data-testid="id-quantity-input"
                  />
                </div>
                <div className="flex items-end">
                  <div className="w-full bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <div className="flex justify-between text-sm mb-1"><span className="text-slate-500">Per ID</span><span className="font-medium">{formatPrice(perIdPrice())}</span></div>
                    <div className="flex justify-between text-lg font-bold"><span>Total</span><span data-testid="academic-total-price">{formatPrice(academicTotal)}</span></div>
                  </div>
                </div>
              </div>

              <p className="text-xs text-slate-500 mb-4">
                On successful payment, {quantity} {category} access ID{quantity > 1 ? 's' : ''} will be emailed to <strong>{instituteEmail || 'your institute email'}</strong>. Each ID is valid for 365 days from generation and can be redeemed by a single {category} at <code>/redeem</code>.
              </p>

              <button
                onClick={handleAcademicSubscribe}
                disabled={loading || (user && (!instituteName.trim() || !instituteEmail.trim() || quantity < 1))}
                className="w-full px-6 py-3.5 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50 transition-all"
                data-testid="academic-subscribe-btn"
              >
                {loading ? 'Processing…' : `Pay ${formatPrice(academicTotal)} & generate IDs`}
              </button>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto" data-testid="corporate-panel">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {corpPackages().map((pkg) => (
                  <div
                    key={pkg.id}
                    onClick={() => setSelectedTenure(pkg.id)}
                    className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all bg-white ${selectedTenure === pkg.id ? 'border-slate-900 shadow-lg' : 'border-slate-200 hover:border-slate-300'}`}
                    data-testid={`package-${pkg.id}`}
                  >
                    {pkg.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-orange-600 text-white text-xs font-medium rounded-full">Most Popular</div>
                    )}
                    <h3 className="text-xl font-serif font-bold mb-2">{pkg.label}</h3>
                    <p className="text-3xl font-bold text-slate-900 mb-2" data-testid={`price-${pkg.id}`}>{formatPrice(pkg.price)}</p>
                    <p className="text-sm text-slate-500">{pkg.description}</p>
                    {selectedTenure === pkg.id && (
                      <div className="absolute top-4 right-4 w-6 h-6 bg-slate-900 rounded-full flex items-center justify-center"><Check className="w-4 h-4 text-white" /></div>
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={handleCorporateSubscribe}
                disabled={loading}
                className="px-8 py-3.5 mx-auto block bg-orange-600 text-white rounded-lg text-lg font-medium hover:bg-orange-700 disabled:opacity-50 transition-all"
                data-testid="subscribe-button"
              >
                {loading ? 'Processing…' : 'Pay Now'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
